import { DialogActions } from '@mui/material';
import DialogContent from '@mui/material/DialogContent';
import React, { useState } from 'react';
import * as Yup from 'yup';

import { Formik } from 'formik';

import { useTranslation } from 'next-i18next';

import Button from 'src/components/ui/Button';
import Dialog from 'src/components/ui/Dialog';
import DialogTitle from 'src/components/ui/DialogTitle';
import Typography from 'src/components/ui/Typography';
import TextField from 'src/components/ui/TextField';
import Loader from 'src/components/ui/Loader';

import useAlerts from 'src/hooks/useAlerts';
import type { ModalProps } from 'src/hooks/useModalOpenState';

import {
    useReportStoryMutation,
    useReportCommentMutation,
    useReportPostMutation,
    useReportAdMutation,
    useReportUserMutation,
    useReportNewsPostMutation,
    useReportConversationMutation,
    useReportCompanyMutation,
} from 'src/hooks/__generated__/queries';

import stopPropagation from 'src/utils/stopPropagation';
import { styledComponent } from 'src/utils/styled';
import { AnalyticsEvent } from 'src/utils/analytics';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';

const StyledDialogTitle = styledComponent(DialogTitle)`
    padding: 1.5rem 1.5rem 0 1.5rem;
`;

const StyledDialog = styledComponent(Dialog)``;

const StyledDialogActions = styledComponent(DialogActions)`
    padding: 0 1.5rem 1.5rem 1.5rem;
`;

StyledDialog.defaultProps = {
    fullWidth: true,
    maxWidth: 'xs',
};
interface Props extends ModalProps {
    storyId?: string;
    postId?: string;
    commentId?: string;
    adId?: string;
    userId?: string;
    newsPostId?: string;
    companyId?: string;
    channelUrl?: string;
    stopTextfieldPropagation?: boolean;
    dataTargetId?: string;
}
type ViewTypes = 'report' | 'confirm';

const ReportModal: React.FC<Props> = ({
    open,
    storyId,
    onClose,
    postId,
    commentId,
    adId,
    userId,
    newsPostId,
    companyId,
    channelUrl,
    stopTextfieldPropagation,
    dataTargetId,
}) => {
    const { t } = useTranslation(['common']);
    const { addAlert } = useAlerts();
    const { logEventDynamic } = useDynamicLogEvents();

    const [selectedView, setSelectedView] = useState<ViewTypes>('report');

    const [reportStoryMutation, { loading: loadingStory }] = useReportStoryMutation();
    const [reportPostMutation, { loading: loadingPost }] = useReportPostMutation();
    const [reportCommentMutation, { loading: loadingComment }] = useReportCommentMutation();
    const [reportAdMutation, { loading: loadingAd }] = useReportAdMutation();
    const [reportUserMutation, { loading: loadingUser }] = useReportUserMutation();
    const [reportNewsPostMutation, { loading: loadingNewsPost }] = useReportNewsPostMutation();
    const [reportConversation, { loading: loadingConversation }] = useReportConversationMutation();
    const [reportCompanyMutation, { loading: loadingCompany }] = useReportCompanyMutation();

    const schema = Yup.object().shape({
        reason: Yup.string().test('minLength', t('common:report.reportExplanationMustBeProvided'), (reason: unknown) =>
            Boolean(reason && (reason as string).trim().length > 0),
        ),
    });

    const handleClose = () => {
        setSelectedView('report');
        onClose();
    };

    const handleSubmitForm = async ({ reason }: { reason?: string }) => {
        try {
            if (reason && reason.trim().length > 0) {
                if (storyId) {
                    await reportStoryMutation({
                        variables: {
                            reason,
                            storyId,
                        },
                    });
                }
                if (postId) {
                    await reportPostMutation({
                        variables: {
                            reason,
                            postId,
                        },
                    });
                }
                if (commentId) {
                    await reportCommentMutation({
                        variables: {
                            reason,
                            commentId,
                        },
                    });
                }
                if (adId) {
                    await reportAdMutation({
                        variables: {
                            reason,
                            adId,
                        },
                    });
                }
                if (userId) {
                    await reportUserMutation({
                        variables: {
                            reason,
                            userId,
                        },
                    });
                }
                if (newsPostId) {
                    await reportNewsPostMutation({
                        variables: {
                            reason,
                            newsPostId,
                        },
                    });
                }
                if (companyId) {
                    await reportCompanyMutation({
                        variables: {
                            reason,
                            companyId,
                        },
                    });
                }
                if (channelUrl) {
                    await reportConversation({
                        variables: {
                            reason,
                            channelUrl,
                        },
                    });
                }
                setSelectedView('confirm');
                logEventDynamic(AnalyticsEvent.Report);
            }
        } catch (error: unknown) {
            addAlert({
                type: 'error',
                message: String(error),
            });
        }
    };
    const loading =
        loadingStory ||
        loadingPost ||
        loadingComment ||
        loadingAd ||
        loadingUser ||
        loadingNewsPost ||
        loadingCompany ||
        loadingConversation;
    return (
        <StyledDialog open={open} onClose={handleClose} data-target-id={dataTargetId}>
            <Formik<{ reason?: string }>
                initialValues={{ reason: undefined }}
                onSubmit={handleSubmitForm}
                validationSchema={schema}
                validateOnMount
            >
                {({ handleSubmit, isValid }) => {
                    let title;
                    let content;
                    let actions;
                    let reportTitle = '';
                    if (storyId) {
                        reportTitle = t('common:report.reportStory');
                    } else if (postId) {
                        reportTitle = t('common:report.reportPost');
                    } else if (commentId) {
                        reportTitle = t('common:report.reportComment');
                    } else if (adId) {
                        reportTitle = t('common:report.reportAd');
                    } else if (userId) {
                        reportTitle = t('common:report.reportUser');
                    } else if (newsPostId) {
                        reportTitle = t('common:report.reportNewsPost');
                    } else if (channelUrl) {
                        reportTitle = t('common:report.reportConversation');
                    } else if (companyId) {
                        reportTitle = t('common:report.reportCompany');
                    }
                    switch (selectedView) {
                        case 'report':
                            title = reportTitle;
                            content = (
                                <TextField
                                    onClick={stopTextfieldPropagation ? stopPropagation : undefined}
                                    onMouseDown={stopTextfieldPropagation ? stopPropagation : undefined}
                                    name="reason"
                                    minRows={3}
                                    placeholder={t('common:report.reportExplanations')}
                                    multiline
                                    hideErrors
                                />
                            );
                            actions = (
                                <>
                                    <Button onClick={handleClose} variant="outlined">
                                        {t('common:modal.cancel')}
                                    </Button>
                                    <Button onClick={() => handleSubmit()} disabled={!isValid}>
                                        {t('common:modal.confirm')}
                                    </Button>
                                </>
                            );
                            break;
                        case 'confirm':
                            title = t('common:report.reportConfirmTitle');
                            content = (
                                <Typography variant="body1">{t('common:report.reportConfirmContent')}</Typography>
                            );
                            actions = <Button onClick={handleClose}>{t('common:modal.ok')}</Button>;
                            break;
                        default:
                            break;
                    }

                    return (
                        <>
                            <StyledDialogTitle>
                                <Typography variant="h2">{title}</Typography>
                            </StyledDialogTitle>
                            <DialogContent>
                                {loading && <Loader />}
                                {!loading && content}
                            </DialogContent>
                            <StyledDialogActions>{actions}</StyledDialogActions>
                        </>
                    );
                }}
            </Formik>
        </StyledDialog>
    );
};

export default ReportModal;
